// Workflow and approval actions
import CreateApprovalWorkflow from "./CreateApprovalWorkflow"
import ActionApproval from "./ActionApproval"

// Interfaces
import { I_CreateApprovalWorkflow, I_ActionApproval, I_ApprovalStep } from "../interfaces"

// Utilities
import snackBar from "src/utilities/snackBar/SnackBar"
import AxiosConfig from "src/utilities/AxiosConfig"

/**
 * Creates an approval workflow and immediately approves it on behalf of the user.
 *
 * @param workflowPayload - The payload used to create the approval workflow
 * @param approverUserId - The ID of the user who is auto-approving the workflow
 * @returns A boolean indicating success or failure
 */
const CreateAndActionWorkflow = async (
  workflowPayload: I_CreateApprovalWorkflow,
  approverUserId: number,
  approvalActionTypeId: number,
  comments?: string
): Promise<boolean> => {
  // Step 1: Create the approval workflow
  const created = await CreateApprovalWorkflow(workflowPayload)
  if (!created) return false

  try {
    // Step 2: Retrieve approval step data to find the correct step for the approver
    const approvalStepsRes = await fetch(
      `${process.env.REACT_APP_API_URL}/api/approval/status?recordTypeId=${workflowPayload.RecordTypeId}&recordId=${workflowPayload.RecordId}`,
      {
        headers: {
          ...AxiosConfig.configValue("min").headers
        }
      }
    )

    const stepData = await approvalStepsRes.json()

    // Step 3: Find the first step for the current approver
    const firstStep = stepData?.Data?.find(
      (s: I_ApprovalStep) => s.StepOrder === 1 && s.ApproverUserId === approverUserId
    )

    if (!firstStep?.ApprovalStepId) {
      snackBar({
        type: "error",
        text: "No matching approval step found",
        timeout: 4000
      })
      return false
    }

    // Step 4: Approve the workflow using the approval step
    const actionPayload: I_ActionApproval = {
      ApprovalStepId: firstStep.ApprovalStepId,
      ActionedBy: approverUserId,
      ApprovalActionTypeId: approvalActionTypeId,
      Comments: comments || null
    }

    return await ActionApproval(actionPayload)
  } catch (error) {
    console.error("CreateAndActionWorkflow failed:", error)
    snackBar({
      type: "error",
      text: "Failed to auto-approve",
      timeout: 4000
    })
    return false
  }
}

export default CreateAndActionWorkflow
